import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { AdornisContext } from '@adornis/baseql/server/context.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { Contract } from '../../contracts/entities/Contract.js';
import { ContractFile } from '../../contracts/entities/ContractFile.js';
import { ContractSupplement } from '../../contracts/entities/ContractSupplement.js';
import { ContractSupplementFile } from '../../contracts/entities/ContractSupplementFile.js';
import { type Permission, Role } from '../../permissions/roles.js';
import { hasUserRole } from '../../permissions/server/hasUserRole.js';
import { Person } from '../../users/entities/person.js';

export function hasPermission(
  currentUser: NarrowedEntity<Person, { _id: 1; roles: { name: 1; contexts: 1; permissions: 1 } }>,
  user: NarrowedEntity<Person, { _id: 1; roles: { name: 1; contexts: 1; permissions: 1 } }>,
  permission: Permission,
) {
  const ids = getSchulIDs(user);
  for (const schulID of ids) {
    if (currentUser.hasPermission(permission, schulID)) return true;
  }
  return currentUser.hasPermission(permission, GLOBAL_CONTEXT);
}

export const getSchulIDs = (user: NarrowedEntity<Person, { roles: { name: 1; contexts: 1 } }>) =>
  Array.from(
    new Set(
      user.roles
        .filter(
          role =>
            role.name === Role.MITARBEITER ||
            role.name === Role.OLD_EMPLOYEE ||
            role.name === Role.KANDIDAT ||
            role.name === Role.OLD_SORGEBERECHTIGTER ||
            role.name === Role.SORGEBERECHTIGTER ||
            role.name === Role.SCHUELER ||
            role.name === Role.SPENDER ||
            role.name === Role.OLD_SPENDER ||
            role.name === Role.SUS_BEWERBER ||
            role.name === Role.SUS_BEWERBER_SB,
        )
        .flatMap(role => role.contexts),
    ),
  );

/**
 * The lookups below deliberately read raw: they answer "who does this row belong to", which decides the
 * permission. Reading them through the publish filter would make the answer depend on what the current user
 * may already see, and a row they cannot see yet would silently read as "belongs to nobody" - a denial
 * instead of an honest school comparison.
 */
const getPersonIDFromNachtragID = async (nachtragID: string) => {
  const nachtrag = await (
    await getRawCollection<EntityData<ContractSupplement>>(ContractSupplement._collectionName)
  ).findOne({ _id: nachtragID }, { projection: { vertragID: 1 } });
  if (!nachtrag) return null;
  return getPersonIDFromVertragID(nachtrag.vertragID);
};

const getPersonIDFromVertragID = async (vertragID: string) => {
  const vertrag = await (
    await getRawCollection<EntityData<Contract>>(Contract._collectionName)
  ).findOne({ _id: vertragID }, { projection: { userID: 1 } });
  if (!vertrag) return null;
  return vertrag.userID;
};

export async function hasPermissionOnEntity(
  currentUser: Maybe<NarrowedEntity<Person, { _id: 1; roles: { name: 1; contexts: 1; permissions: 1 } }>>,
  entityData: any,
  entityClass: string,
  ...permissions: Permission[]
) {
  if (!currentUser || !entityData) return false;
  let affectedUserID: string | null = null;
  switch (entityClass) {
    case Contract._class:
      affectedUserID = entityData.userID;
      break;
    case ContractFile._class:
      affectedUserID = await getPersonIDFromVertragID(entityData.contractID);
      break;
    case ContractSupplement._class:
      affectedUserID = await getPersonIDFromVertragID(entityData.vertragID);
      break;
    case ContractSupplementFile._class:
      affectedUserID = await getPersonIDFromNachtragID(entityData.supplementID);
      break;

    case Person._class:
      affectedUserID = entityData._id;
      break;
  }
  // a row without an owner is refused, but that is missing data and not a missing permission - say so, the
  // caller only ever sees the generic NotAuthorizedError
  if (!affectedUserID) {
    logger.error({ entityClass }, 'Betroffene Person der Entität nicht ermittelbar, Operation wird abgelehnt.');
    return false;
  }
  if (affectedUserID === currentUser._id) return true;

  const entityUser = await (
    await getRawCollection<EntityData<Person>>(Person._collectionName)
  ).findOne({ _id: affectedUserID }, { projection: { _id: 1, roles: { name: 1, contexts: 1 } } });

  const schulIds = entityUser?.roles.flatMap(r => r.contexts);
  if (!schulIds) logger.error(`No role contexts found for user with ${affectedUserID}!`);

  return (schulIds ?? []).some(sId =>
    permissions.some(
      permission => currentUser.hasPermission(permission, sId) || currentUser.hasPermission(permission, GLOBAL_CONTEXT),
    ),
  );
}

export function getEntityPermission(
  context: AdornisContext,
  currentUser: Maybe<NarrowedEntity<Person, { _id: 1 }>>,
  entityClass: string,
  ...permissions: Permission[]
) {
  if (context.serverContext) return { create: true, modify: true, delete: true };
  if (!currentUser) return { create: false, modify: false, delete: false };
  return {
    create: async (entityData: any) => {
      const isAdmin = await hasUserRole(currentUser._id, Role.ADMIN, GLOBAL_CONTEXT);
      if (isAdmin) return true;
      return hasPermissionOnEntity(currentUser, entityData, entityClass, ...permissions);
    },
    modify: async (oldEntityData: any) => {
      const isAdmin = await hasUserRole(currentUser._id, Role.ADMIN, GLOBAL_CONTEXT);
      if (isAdmin) return true;
      return hasPermissionOnEntity(currentUser, oldEntityData, entityClass, ...permissions);
    },
    delete: async (entityData: any) => {
      const isAdmin = await hasUserRole(currentUser._id, Role.ADMIN, GLOBAL_CONTEXT);
      if (isAdmin) return true;
      return hasPermissionOnEntity(currentUser, entityData, entityClass, ...permissions);
    },
  };
}
